import React from "react";
import { getStartOfWeek, getEndOfWeek } from "utils/dateTime";
import type { ViewMode } from "components/common/PlanningCalendar";

export function useExcursionCalendar() {
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [viewMode, setViewMode] = React.useState<ViewMode>("week");

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) setViewMode("day");
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navigatePeriod = React.useCallback(
    (direction: "prev" | "next") => {
      setCurrentDate((prev) => {
        const next = new Date(prev);
        if (viewMode === "month") {
          next.setMonth(next.getMonth() + (direction === "next" ? 1 : -1));
        } else if (viewMode === "day") {
          next.setDate(next.getDate() + (direction === "next" ? 1 : -1));
        } else {
          next.setDate(next.getDate() + (direction === "next" ? 7 : -7));
        }
        return next;
      });
    },
    [viewMode],
  );

  const formatPeriodTitle = React.useCallback((): string => {
    if (viewMode === "month") {
      return currentDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    }
    if (viewMode === "day") {
      return currentDate.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    const start = getStartOfWeek(currentDate);
    const end = getEndOfWeek(currentDate);
    return `${start.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} - ${end.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`;
  }, [currentDate, viewMode]);

  const days = React.useMemo((): Date[] => {
    if (viewMode === "month") {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const out: Date[] = [];
      for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
        out.push(new Date(d));
      }
      return out;
    }
    if (viewMode === "day") {
      return [new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())];
    }
    const start = getStartOfWeek(currentDate);
    const out: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      out.push(day);
    }
    return out;
  }, [currentDate, viewMode]);

  const monthGridOffset = React.useMemo((): number => {
    if (viewMode !== "month") return 0;
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    return (firstDay.getDay() + 6) % 7;
  }, [currentDate, viewMode]);

  return {
    currentDate,
    setCurrentDate,
    viewMode,
    setViewMode,
    days,
    monthGridOffset,
    formatPeriodTitle,
    navigatePeriod,
  };
}
